<template>
  <v-card flat dark class="corrosion-range-bar" color="transparent">
    <!-- Header -->
    <div class="range-bar-header mb-3">
      <div class="d-flex align-center justify-space-between mb-2">
        <span class="text-subtitle-2 font-weight-bold">
          <v-icon small class="mr-1">mdi-gradient-vertical</v-icon>
          Color Range Configuration
        </span>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              x-small
              @click="resetToDefaults"
              v-on="on"
            >
              <v-icon small>mdi-restore</v-icon>
            </v-btn>
          </template>
          <span>Reset to defaults</span>
        </v-tooltip>
      </div>

      <!-- Preset Buttons -->
      <div class="preset-buttons mb-3">
        <v-chip-group
          v-model="selectedPreset"
          active-class="primary--text"
          mandatory
          @change="applyPreset"
        >
          <v-chip small outlined>
            <v-icon x-small left>mdi-speedometer</v-icon>
            Standard
          </v-chip>
        </v-chip-group>
      </div>
    </div>

    <!-- Gradient Preview Bar -->
    <div class="gradient-preview-container mb-4">
      <div class="gradient-preview-bar" :style="gradientStyle"></div>
      <div class="gradient-labels">
        <span class="text-caption">0mm</span>
        <span class="text-caption">{{ maxThreshold }}mm</span>
      </div>
    </div>

    <!-- Threshold Sliders -->
    <div class="threshold-controls">
      <!-- Normal to Minor Wear Threshold -->
      <div class="threshold-item mb-3">
        <div class="d-flex align-center justify-space-between mb-1">
          <div class="d-flex align-center">
            <v-checkbox
              v-model="visibleClassifications.normal"
              hide-details
              dense
              color="success"
              class="visibility-checkbox ma-0 pa-0 mr-1"
              @change="emitVisibilityChanged"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small color="success" class="mr-2">mdi-circle</v-icon>
                  <span class="text-caption font-weight-medium">Normal → Minor Wear</span>
                </div>
              </template>
            </v-checkbox>
          </div>
          <v-text-field
            v-model.number="thresholds.normalToMinor"
            type="number"
            dense
            outlined
            hide-details
            suffix="mm"
            class="threshold-input"
            :min="0.1"
            :max="thresholds.minorToModerate - 0.1"
            step="0.1"
            disabled
            @input="validateAndEmit"
          ></v-text-field>
        </div>
        <div class="slider-row">
          <span class="range-label">0.1</span>
          <v-slider
            v-model="thresholds.normalToMinor"
            :min="0.1"
            :max="thresholds.minorToModerate - 0.1"
            step="0.1"
            color="success"
            track-color="grey lighten-2"
            hide-details
            disabled
            class="threshold-slider"
            @change="validateAndEmit"
          ></v-slider>
          <span class="range-label">{{ (thresholds.minorToModerate - 0.1).toFixed(1) }}</span>
        </div>
      </div>

      <!-- Minor Wear to Moderate Threshold -->
      <div class="threshold-item mb-3">
        <div class="d-flex align-center justify-space-between mb-1">
          <div class="d-flex align-center">
            <v-checkbox
              v-model="visibleClassifications.minorWear"
              hide-details
              dense
              color="warning"
              class="visibility-checkbox ma-0 pa-0 mr-1"
              @change="emitVisibilityChanged"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small color="warning" class="mr-2">mdi-circle</v-icon>
                  <span class="text-caption font-weight-medium">Minor Wear → Moderate</span>
                </div>
              </template>
            </v-checkbox>
          </div>
          <v-text-field
            v-model.number="thresholds.minorToModerate"
            type="number"
            dense
            outlined
            hide-details
            suffix="mm"
            class="threshold-input"
            :min="thresholds.normalToMinor + 0.1"
            :max="thresholds.moderateToCritical - 0.1"
            step="0.1"
            disabled
            @input="validateAndEmit"
          ></v-text-field>
        </div>
        <div class="slider-row">
          <span class="range-label">{{ (thresholds.normalToMinor + 0.1).toFixed(1) }}</span>
          <v-slider
            v-model="thresholds.minorToModerate"
            :min="thresholds.normalToMinor + 0.1"
            :max="thresholds.moderateToCritical - 0.1"
            step="0.1"
            color="warning"
            track-color="grey lighten-2"
            hide-details
            disabled
            class="threshold-slider"
            @change="validateAndEmit"
          ></v-slider>
          <span class="range-label">{{ (thresholds.moderateToCritical - 0.1).toFixed(1) }}</span>
        </div>
      </div>

      <!-- Moderate to Critical Threshold -->
      <div class="threshold-item mb-3">
        <div class="d-flex align-center justify-space-between mb-1">
          <div class="d-flex align-center">
            <v-checkbox
              v-model="visibleClassifications.moderate"
              hide-details
              dense
              color="orange"
              class="visibility-checkbox ma-0 pa-0 mr-1"
              @change="emitVisibilityChanged"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small color="orange" class="mr-2">mdi-circle</v-icon>
                  <span class="text-caption font-weight-medium">Moderate → Critical</span>
                </div>
              </template>
            </v-checkbox>
          </div>
          <v-text-field
            v-model.number="thresholds.moderateToCritical"
            type="number"
            dense
            outlined
            hide-details
            suffix="mm"
            class="threshold-input"
            :min="thresholds.minorToModerate + 0.1"
            :max="20"
            step="0.1"
            disabled
            @input="validateAndEmit"
          ></v-text-field>
        </div>
        <div class="slider-row">
          <span class="range-label">{{ (thresholds.minorToModerate + 0.1).toFixed(1) }}</span>
          <v-slider
            v-model="thresholds.moderateToCritical"
            :min="thresholds.minorToModerate + 0.1"
            :max="20"
            step="0.1"
            color="orange"
            track-color="grey lighten-2"
            hide-details
            disabled
            class="threshold-slider"
            @change="validateAndEmit"
          ></v-slider>
          <span class="range-label">20.0</span>
        </div>
      </div>

      <!-- Critical Corrosion (above moderateToCritical threshold) -->
      <div class="threshold-item mb-3">
        <div class="d-flex align-center justify-space-between mb-1">
          <div class="d-flex align-center">
            <v-checkbox
              v-model="visibleClassifications.critical"
              hide-details
              dense
              color="error"
              class="visibility-checkbox ma-0 pa-0 mr-1"
              @change="emitVisibilityChanged"
            >
              <template v-slot:label>
                <div class="d-flex align-center">
                  <v-icon small color="error" class="mr-2">mdi-circle</v-icon>
                  <span class="text-caption font-weight-medium">Critical Corrosion</span>
                </div>
              </template>
            </v-checkbox>
          </div>
          <div class="threshold-value-display">
            {{ thresholds.moderateToCritical }}mm+
          </div>
        </div>
      </div>

      <!-- Max Display Range -->
      <div class="threshold-item">
        <div class="d-flex align-center justify-space-between mb-1">
          <div class="d-flex align-center">
            <v-icon small color="grey" class="mr-2">mdi-chart-line</v-icon>
            <span class="text-caption font-weight-medium">Max Display Range</span>
          </div>
          <v-text-field
            v-model.number="thresholds.maxDisplay"
            type="number"
            dense
            outlined
            hide-details
            suffix="mm"
            class="threshold-input"
            :min="thresholds.moderateToCritical + 0.1"
            :max="30"
            step="0.5"
            @input="validateAndEmit"
            disabled
          ></v-text-field>
        </div>
        <div class="slider-row">
          <span class="range-label">{{ (thresholds.moderateToCritical + 0.1).toFixed(1) }}</span>
          <v-slider
            v-model="thresholds.maxDisplay"
            :min="thresholds.moderateToCritical + 0.1"
            :max="30"
            step="0.5"
            color="error"
            track-color="grey lighten-2"
            hide-details
            disabled
            class="threshold-slider"
            @change="validateAndEmit"
          ></v-slider>
          <span class="range-label">30.0</span>
        </div>
      </div>
    </div>

    <!-- Info Alert -->
    <v-alert
      dense
      text
      type="info"
      class="mt-3 mb-0"
      icon="mdi-information-outline"
    >
      <span class="text-caption">
        Adjust thresholds to customize severity levels. Changes apply in real-time.
      </span>
    </v-alert>

    <!-- Apply Button (if not auto-apply) -->
    <div v-if="!autoApply" class="mt-3">
      <v-btn
        block
        small
        color="primary"
        @click="applyThresholds"
        :disabled="!hasChanges"
      >
        <v-icon small left>mdi-check</v-icon>
        Apply Changes
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import { log as debugLog } from '@/utils/debugLogger'

export default {
  name: 'CorrosionColorRangeBar',

  props: {
    // Initial threshold values (in mm)
    // Initial thresholds (in mm) aligned with enums.js category boundaries
    initialThresholds: {
      type: Object,
      default: () => ({
        normalToMinor: 2.0,   // Cat A -> Cat B
        minorToModerate: 4.0,  // Cat B -> Cat C
        moderateToCritical: 6.0, // Cat C -> Cat D
        maxDisplay: 15.0
      })
    },
    // Auto-apply changes without explicit button click
    autoApply: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      // Default thresholds aligned with enums.js category boundaries
      thresholds: {
        normalToMinor: 2.0,   // Cat A -> Cat B
        minorToModerate: 4.0,  // Cat B -> Cat C
        moderateToCritical: 6.0, // Cat C -> Cat D
        maxDisplay: 15.0
      },
      originalThresholds: null,
      selectedPreset: 0, // 0: Standard, 1: Sensitive, 2: Tolerant
      // Preset configurations aligned with category thresholds from enums.js
      presets: {
        standard: {
          normalToMinor: 2.0,   // Cat A -> Cat B boundary (thresholdThicknessLoss: 2mm)
          minorToModerate: 4.0,  // Cat B -> Cat C boundary (thresholdThicknessLoss: 4mm)
          moderateToCritical: 6.0, // Cat C -> Cat D boundary (thresholdThicknessLoss: 6mm)
          maxDisplay: 15.0
        },
        sensitive: {
          normalToMinor: 1.0,   // Earlier detection
          minorToModerate: 2.5,
          moderateToCritical: 4.0,
          maxDisplay: 12.0
        },
        tolerant: {
          normalToMinor: 4.0,   // More lenient
          minorToModerate: 6.0,
          moderateToCritical: 9.0,
          maxDisplay: 20.0
        }
      },
      // Visibility control for each classification level
      // Ranges updated to match new thresholds
      visibleClassifications: {
        normal: true,      // 0-2mm (Cat A - Green)
        minorWear: true,   // 2-4mm (Cat B - Yellow)
        moderate: true,    // 4-6mm (Cat C - Orange)
        critical: true     // 6mm+ (Cat D - Red)
      }
    }
  },

  computed: {
    /**
     * Calculate max threshold for display
     */
    maxThreshold() {
      return this.thresholds.maxDisplay
    },

    /**
     * Generate CSS gradient style for preview bar
     */
    gradientStyle() {
      const { normalToMinor, minorToModerate, moderateToCritical, maxDisplay } = this.thresholds

      // Calculate percentage positions
      const pos1 = (normalToMinor / maxDisplay) * 100
      const pos2 = (minorToModerate / maxDisplay) * 100
      const pos3 = (moderateToCritical / maxDisplay) * 100

      return {
        background: `linear-gradient(to right,
          #00FF00 0%,
          #00FF00 ${pos1 * 0.8}%,
          #FFFF00 ${pos1}%,
          #FFFF00 ${pos2 * 0.9}%,
          #FF8800 ${pos2}%,
          #FF8800 ${pos3 * 0.95}%,
          #FF0000 ${pos3}%,
          #FF0000 100%
        )`
      }
    },

    /**
     * Check if thresholds have changed
     */
    hasChanges() {
      if (!this.originalThresholds) return false
      return JSON.stringify(this.thresholds) !== JSON.stringify(this.originalThresholds)
    }
  },

  watch: {
    initialThresholds: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.thresholds = { ...newVal }
          this.originalThresholds = { ...newVal }
        }
      }
    }
  },

  methods: {
    /**
     * Validate thresholds and emit changes
     */
    validateAndEmit() {
      // Ensure proper ordering
      if (this.thresholds.normalToMinor >= this.thresholds.minorToModerate) {
        this.thresholds.normalToMinor = this.thresholds.minorToModerate - 0.1
      }
      if (this.thresholds.minorToModerate >= this.thresholds.moderateToCritical) {
        this.thresholds.minorToModerate = this.thresholds.moderateToCritical - 0.1
      }
      if (this.thresholds.moderateToCritical >= this.thresholds.maxDisplay) {
        this.thresholds.moderateToCritical = this.thresholds.maxDisplay - 0.5
      }

      // Round to 1 decimal place
      Object.keys(this.thresholds).forEach(key => {
        this.thresholds[key] = parseFloat(this.thresholds[key].toFixed(1))
      })

      if (this.autoApply) {
        this.$emit('thresholds-changed', this.getThresholdsInMeters())
      }
    },

    /**
     * Apply thresholds manually (when autoApply is false)
     */
    applyThresholds() {
      this.$emit('thresholds-changed', this.getThresholdsInMeters())
      this.originalThresholds = { ...this.thresholds }
    },

    /**
     * Convert thresholds from mm to meters for the shader
     */
    getThresholdsInMeters() {
      return {
        normalToMinor: this.thresholds.normalToMinor / 1000, // mm to m
        minorToModerate: this.thresholds.minorToModerate / 1000,
        moderateToCritical: this.thresholds.moderateToCritical / 1000,
        maxDisplay: this.thresholds.maxDisplay / 1000
      }
    },

    /**
     * Reset to default thresholds
     */
    resetToDefaults() {
      this.selectedPreset = 0
      this.thresholds = { ...this.presets.standard }
      this.validateAndEmit()
      this.$emit('reset')
    },

    /**
     * Apply preset configuration
     */
    applyPreset(presetIndex) {
      const presetNames = ['standard', 'sensitive', 'tolerant']
      const presetName = presetNames[presetIndex]

      if (this.presets[presetName]) {
        this.thresholds = { ...this.presets[presetName] }
        this.validateAndEmit()
        this.$emit('preset-applied', presetName)
      }
    },

    /**
     * Emit visibility changed event
     * Called when any classification visibility checkbox is toggled
     */
    emitVisibilityChanged() {
      debugLog('[CorrosionRangeBar] Visibility changed:', this.visibleClassifications)
      this.$emit('visibility-changed', { ...this.visibleClassifications })
    },

    /**
     * Toggle all classifications visibility
     * @param {boolean} visible - Whether all should be visible
     */
    toggleAllClassifications(visible) {
      this.visibleClassifications.normal = visible
      this.visibleClassifications.minorWear = visible
      this.visibleClassifications.moderate = visible
      this.visibleClassifications.critical = visible
      this.emitVisibilityChanged()
    },

    /**
     * Get current visibility settings
     * @returns {Object} Current visibility settings
     */
    getVisibilitySettings() {
      return { ...this.visibleClassifications }
    }
  }
}
</script>

<style scoped lang="scss">
/* ============================================
   CORROSION COLOR RANGE BAR - Professional CAD Style
   ============================================ */

.corrosion-range-bar {
  --bg-panel: #2b2b2b;
  --bg-elevated: #323232;
  --bg-card: #363636;
  --border-color: #4a4a4a;
  --border-subtle: #3d3d3d;
  --text-primary: #e0e0e0;
  --text-secondary: #a0a0a0;
  --text-muted: #707070;
  /* Green accent - Unified with toolbar (#03624c family) */
  --accent-primary: #03624c;
  --accent-primary-light: #a8c97a;
  --radius: 4px;

  background: transparent;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.range-bar-header {
  .text-subtitle-2 {
    color: var(--text-primary);
    font-size: 12px;
    font-weight: 600;
  }
}

.preset-buttons {
  .v-chip {
    font-size: 10px;
    height: 22px;
    background: var(--bg-elevated) !important;
    border: 1px solid var(--border-color) !important;
    color: var(--text-secondary) !important;
  }
}

.gradient-preview-container {
  position: relative;
}

.gradient-preview-bar {
  width: 100%;
  height: 24px;
  border-radius: var(--radius);
  border: 1px solid var(--border-color);
}

.gradient-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 4px;
  padding: 0 4px;

  .text-caption {
    font-size: 10px;
    font-weight: 500;
    color: var(--text-muted);
  }
}

.threshold-controls {
  .threshold-item {
    padding: 10px;
    background: var(--bg-elevated);
    border-radius: var(--radius);
    border: 1px solid var(--border-subtle);
    transition: all 150ms ease;

    &:hover {
      border-color: var(--border-color);
    }
  }

  .visibility-checkbox {
    flex: 1;

    ::v-deep .v-input__slot {
      margin-bottom: 0 !important;
    }

    ::v-deep .v-label {
      font-size: 11px;
      color: #e0e0e0 !important;
    }

    ::v-deep .v-input--selection-controls__input {
      margin-right: 4px;
    }

    .text-caption {
      color: #e0e0e0 !important;
    }
  }

  .threshold-input {
    max-width: 80px;
    margin-left: auto;

    ::v-deep .v-input__slot {
      min-height: 28px !important;
      background: var(--bg-card) !important;
      border-color: var(--border-color) !important;
    }

    ::v-deep .v-text-field__suffix {
      font-size: 10px;
      color: var(--text-muted);
    }

    ::v-deep input {
      font-size: 11px;
      text-align: right;
      padding: 4px 6px;
      color: var(--text-primary);
    }
  }

  /* Slider Row - Horizontal alignment fix */
  .slider-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
  }

  .range-label {
    font-size: 11px;
    font-weight: 500;
    color: #b0b0b0;
    min-width: 32px;
    text-align: center;
    flex-shrink: 0;
  }

  .threshold-value-display {
    font-size: 12px;
    font-weight: 500;
    color: #b0b0b0;
    padding: 4px 8px;
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: var(--radius);
  }

  .threshold-slider {
    flex: 1;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .v-slider__thumb {
    width: 14px;
    height: 14px;
  }

  ::v-deep .v-slider__track-fill {
    border-radius: 2px;
  }

  ::v-deep .v-slider__track-background {
    background: var(--bg-card) !important;
  }
}

::v-deep .v-alert {
  background: var(--bg-elevated) !important;
  border: 1px solid var(--border-subtle) !important;

  .v-alert__content {
    color: var(--text-secondary);
  }
}
</style>
